(() => {
  'use strict';
  if (!window.customElements || customElements.get('aroha-labour-weekend')) return;
  class ArohaLabourWeekend extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.abort = new AbortController();
      try { this.initialize(); } catch (_) { this.restoreStatic(); }
    }
    disconnectedCallback() {
      clearInterval(this.rotationTimer);
      clearInterval(this.windowTimer);
      this.abort?.abort();
      this.initialized = false;
    }
    initialize() {
      this.allSlides = [...this.querySelectorAll('.aa-lw-slide')];
      this.slides = [];
      this.viewport = this.querySelector('.aa-lw-slides');
      this.controls = this.querySelector('.aa-lw-controls');
      this.toggle = this.querySelector('[data-lw-toggle]');
      this.position = this.querySelector('[data-lw-position]');
      this.phase = this.querySelector('[data-lw-phase]');
      this.disclosure = this.querySelector('.aa-lw-disclosure');
      this.previous = this.querySelector('[data-lw-previous]');
      this.next = this.querySelector('[data-lw-next]');
      if (!this.allSlides.length || !this.viewport || !this.controls || !this.toggle || !this.position || !this.phase || !this.previous || !this.next || !this.dataset.labelPosition) return;
      this.index = 0;
      this.userPaused = false;
      this.hovering = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this.abort.signal };
      const on = (target, event, listener) => target?.addEventListener(event, listener, options);
      on(this.disclosure, 'toggle', () => { this.equalizeSlideHeight(); this.refreshRotation(); });
      on(this.previous, 'click', () => this.go(this.index - 1, true));
      on(this.next, 'click', () => this.go(this.index + 1, true));
      on(this.toggle, 'pointerdown', () => { this.pointerToggleState = !this.userPaused; });
      on(this.toggle, 'click', () => {
        this.userPaused = this.pointerToggleState ?? !this.userPaused;
        this.pointerToggleState = undefined;
        this.refreshRotation();
      });
      on(this, 'mouseenter', () => { this.hovering = true; this.refreshRotation(); });
      on(this, 'mouseleave', () => { this.hovering = false; this.refreshRotation(); });
      on(this, 'focusin', () => { this.userPaused = true; this.refreshRotation(); });
      on(this, 'keydown', event => {
        if (!this.controls.contains(event.target) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const actions = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in actions)) return;
        event.preventDefault();
        this.go(actions[event.key], true);
      });
      on(document, 'visibilitychange', () => { this.updateWindow(); this.refreshRotation(); });
      on(window, 'pageshow', () => { this.updateWindow(); this.refreshRotation(); });
      on(window, 'resize', () => this.equalizeSlideHeight());
      on(this.motion, 'change', () => this.refreshRotation());
      this.updateWindow();
      this.setAttribute('data-lw-enhanced', '');
      this.setAttribute('aria-roledescription', 'carousel');
      this.initialized = true;
      this.equalizeSlideHeight();
      this.windowTimer = setInterval(() => this.updateWindow(), 1000);
      this.refreshRotation();
    }
    reliefEligible(now) {
      try {
        const config = JSON.parse(document.getElementById('ArohaReliefOfferConfig')?.textContent || 'null');
        const start = Date.parse(config?.startsAt), end = Date.parse(config?.endsAt);
        return config?.ready === true && config.discountId === 'gid://shopify/DiscountAutomaticNode/1330911609009' && config.percent === 20 && String(config.collectionId) === '361683157169' && String(config.themeId) === this.dataset.themeId && Number.isFinite(start) && Number.isFinite(end) && start < end && config.endsAt === '2026-10-28T11:00:00Z' && now >= start && now < end;
      } catch (_) { return false; }
    }
    restoreStatic() {
      clearInterval(this.rotationTimer);
      clearInterval(this.windowTimer);
      this.abort?.abort();
      this.removeAttribute('data-lw-enhanced');
      this.removeAttribute('aria-roledescription');
      const now = Date.now(), start = Date.parse(this.dataset.start), end = Date.parse(this.dataset.end);
      this.hidden = !(Number.isFinite(start) && Number.isFinite(end) && start < end && now < end && ((now >= start && this.dataset.ready === 'true') || (now < start && this.dataset.coming === 'true')));
      this.allSlides?.forEach(slide => { slide.hidden = slide.hasAttribute('data-lw-relief') && !this.reliefEligible(now); slide.removeAttribute('aria-roledescription'); slide.removeAttribute('aria-label'); });
      if (this.controls) this.controls.hidden = true;
      if (this.viewport) this.viewport.removeAttribute('aria-live');
      this.style.removeProperty('--aa-lw-slide-height');
      this.initialized = false;
    }
    equalizeSlideHeight() {
      this.style.removeProperty('--aa-lw-slide-height');
      let height = 0;
      this.slides.forEach(slide => {
        const wasHidden = slide.hidden;
        slide.hidden = false;
        height = Math.max(height, slide.getBoundingClientRect().height);
        slide.hidden = wasHidden;
      });
      this.style.setProperty('--aa-lw-slide-height', `${Math.ceil(height)}px`);
    }
    updateWindow() {
      const now = Date.now(), start = Date.parse(this.dataset.start), end = Date.parse(this.dataset.end);
      const valid = Number.isFinite(start) && Number.isFinite(end) && start < end;
      const live = valid && now >= start && now < end && this.dataset.ready === 'true';
      const coming = valid && now < start && this.dataset.coming === 'true';
      const nextState = live ? 'live' : coming ? 'coming' : 'hidden';
      const stateChanged = this.dataset.state !== nextState;
      this.dataset.state = nextState;
      this.hidden = nextState === 'hidden';
      const current = this.slides[this.index];
      const available = this.allSlides.filter(slide => !slide.hasAttribute('data-lw-relief') || (live && this.reliefEligible(now)));
      const slidesChanged = available.length !== this.slides.length || available.some((slide, index) => slide !== this.slides[index]);
      if (slidesChanged) {
        const returnFocus = current && !available.includes(current) && current.contains(document.activeElement);
        this.slides = available;
        this.go(Math.max(0, available.indexOf(current)), false);
        this.equalizeSlideHeight();
        if (returnFocus && !this.hidden) this.querySelector('.aa-lw-summary')?.focus();
      }
      this.controls.hidden = this.hidden || this.slides.length < 2;
      if (!this.hidden) {
        const phase = live ? this.dataset.labelLive : this.dataset.labelComing;
        if (this.phase.textContent !== phase) this.phase.textContent = phase;
        let currency = this.dataset.currency;
        try { currency = window.Shopify?.currency?.active || currency; } catch (_) {}
        this.querySelectorAll('[data-lw-terms]').forEach(item => {
          const terms = currency === 'AUD' ? this.dataset.termsAud : live ? this.dataset.termsLive : this.dataset.termsComing;
          if (item.textContent !== terms) item.textContent = terms;
        });
      }
      if (stateChanged || slidesChanged) this.refreshRotation();
    }
    go(index, manual) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.allSlides.forEach(slide => { slide.hidden = slide !== this.slides[this.index]; });
      this.slides.forEach((slide, position) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', this.dataset.labelPosition.replace('__CURRENT__', position + 1).replace('__TOTAL__', this.slides.length));
      });
      this.position.textContent = `${this.index + 1} / ${this.slides.length}`;
      if (manual) this.userPaused = true;
      this.refreshRotation();
    }
    refreshRotation() {
      clearInterval(this.rotationTimer);
      if (!this.toggle || !this.viewport || !this.motion) return;
      const running = !this.hidden && (!this.disclosure || this.disclosure.open) && !this.userPaused && !this.hovering && !document.hidden && !this.motion.matches && this.slides.length > 1;
      this.toggle.textContent = this.userPaused ? this.dataset.labelPlay : this.dataset.labelPause;
      this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.labelPlayAction : this.dataset.labelPauseAction);
      this.toggle.hidden = this.motion.matches;
      this.viewport.setAttribute('aria-live', running ? 'off' : 'polite');
      if (running) this.rotationTimer = setInterval(() => this.go(this.index + 1, false), 5000);
    }
  }
  customElements.define('aroha-labour-weekend', ArohaLabourWeekend);
})();
